import { useEffect, useState } from "react";
import { Cloud, CloudRain, Sun, CloudSnow, CloudSun } from "lucide-react";
import { api } from "@/lib/api";

// WMO weather code → icon + label
function iconFor(code) {
  if (code === 0) return { Icon: Sun, label: "Clear" };
  if ([1, 2].includes(code)) return { Icon: CloudSun, label: "Mostly clear" };
  if (code === 3) return { Icon: Cloud, label: "Overcast" };
  if ([45, 48].includes(code)) return { Icon: Cloud, label: "Fog" };
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Rain" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Snow" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudRain, label: "Storm" };
  return { Icon: Cloud, label: "Cloudy" };
}

export default function WeatherPanel() {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!navigator.geolocation) { setState({ error: "Location not supported" }); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/weather", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          setState({ data });
        } catch (e) { setState({ error: "Weather unavailable" }); }
      },
      () => setState({ error: "Enable location for weather" }),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 15 * 60 * 1000 }
    );
  }, []);

  if (state.loading) {
    return (
      <div className="rounded-3xl bg-white border border-gray-100 p-6 premium-shadow">
        <div className="text-xs uppercase tracking-widest text-gray-500">Weather</div>
        <div className="mt-2 text-sm text-gray-500">Loading…</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="rounded-3xl bg-white border border-gray-100 p-6 premium-shadow">
        <div className="text-xs uppercase tracking-widest text-gray-500">Weather</div>
        <div className="mt-2 text-sm text-gray-500">{state.error}</div>
      </div>
    );
  }
  const d = state.data;
  const current = d.current || {};
  const { Icon: NowIcon, label: nowLabel } = iconFor(current.weather_code);
  const daily = d.daily || {};
  const days = (daily.time || []).map((dt, i) => ({
    dt, code: daily.weather_code?.[i], tmax: daily.temperature_2m_max?.[i], tmin: daily.temperature_2m_min?.[i],
    pop: daily.precipitation_probability_max?.[i],
  }));

  return (
    <div data-testid="weather-panel" className="rounded-3xl bg-gradient-to-br from-sky-50 via-emerald-50 to-white border border-emerald-100 p-6 premium-shadow">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.24em] text-emerald-700">Farm Weather</div>
        <div className="text-[10px] text-gray-500">Open-Meteo</div>
      </div>
      <div className="mt-3 flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center">
          <NowIcon className="h-7 w-7" />
        </div>
        <div>
          <div className="text-3xl font-bold text-gray-900">{Math.round(current.temperature_2m ?? 0)}°C</div>
          <div className="text-xs text-gray-500">{nowLabel} · {current.relative_humidity_2m ?? 0}% humidity</div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {days.map((day, i) => {
          const { Icon, label } = iconFor(day.code);
          const date = new Date(day.dt);
          return (
            <div key={day.dt} className="rounded-2xl bg-white border border-gray-100 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{i === 0 ? "Today" : date.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <Icon className="h-5 w-5 mx-auto mt-1 text-emerald-700" />
              <div className="text-sm font-bold text-gray-900 mt-1">{Math.round(day.tmax)}° <span className="text-gray-400 font-normal">/ {Math.round(day.tmin)}°</span></div>
              <div className="text-[10px] text-blue-600">💧 {day.pop ?? 0}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
